import { ConnectionPositionPair, FlexibleConnectedPositionStrategy, Overlay, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { Directive, ElementRef, HostListener, Input, OnDestroy, OnInit, ViewContainerRef } from '@angular/core';
import { AbstractControl, NgControl } from '@angular/forms';

import { fromEvent, Observable, Subject } from 'rxjs';
import { filter, takeUntil, tap } from 'rxjs/operators';

import { AutocompleteComponent } from './autocomplete.component';

@Directive({
    selector: '[aaAutocomplete]',
    standalone: false
})
export class AutocompleteDirective implements OnInit, OnDestroy
{
    @Input('aaAutocomplete') autocomplete: AutocompleteComponent;
    private overlayRef: OverlayRef;
    private destroyator: Subject<unknown> = new Subject();
    private intervalId: NodeJS.Timeout;
    get control (): AbstractControl
    {
        return this.ngControl.control;
    }

    get origin ():HTMLInputElement
    {
        return this.host.nativeElement;
    }

    constructor (
        private host: ElementRef<HTMLInputElement>,
        private ngControl: NgControl,
        private vcr: ViewContainerRef,
        private overlay: Overlay
    )
    {
    }

    ngOnInit (): void
    {
        this.handleFocus();
    }

    ngOnDestroy (): void
    {
        this.destroyator.next(null);
        this.destroyator.complete();
    }

    @HostListener('document:keyup', ['$event'])
    handleKeyDown (event: KeyboardEvent): void
    {
        if (event.key === 'Escape')
        {
            this.close();
        }
    }

    @HostListener('document:visibilitychange', ['$event'])
    handleVisibilityChange (): void
    {
        this.close();
    }

    private handleFocus = (): void =>
    {
        fromEvent(this.origin, 'focus')
            .pipe(
                tap((): void =>
                {
                    this.openDropdown();
                    this.autocomplete.optionsClick()
                        .pipe(
                            takeUntil(this.overlayRef.detachments()),
                            tap((): void =>
                            {
                                this.close();
                            })
                        )
                        .subscribe();
                }),
                takeUntil(this.destroyator)
            )
            .subscribe();
    };

    private openDropdown (): void
    {
        this.overlayRef = this.overlay.create({
            maxHeight: 40 * 3,
            backdropClass: '',
            scrollStrategy: this.overlay.scrollStrategies.close(),
            positionStrategy: this.getOverlayPosition()
        });

        const template = new TemplatePortal(this.autocomplete.rootTemplate, this.vcr);
        this.overlayRef.attach(template);
        this.overlayClickOutside()
            .subscribe(
                (): void =>
                    this.close()
            );
        this.intervalId = setTimeout(
            (): void =>
                this.close(),
            10000
        );
    }

    private close (): void
    {
        this.origin.blur();
        if (this.intervalId)
        {
            clearTimeout(this.intervalId);
        }

        if (this.overlayRef)
        {
            this.overlayRef.detach();
            this.overlayRef = null;
        }
    }

    private getOverlayPosition (): FlexibleConnectedPositionStrategy
    {
        const positions = [
            new ConnectionPositionPair(
                { originX: 'start', originY: 'bottom' },
                { overlayX: 'start', overlayY: 'top' }
            ),
            new ConnectionPositionPair(
                { originX: 'start', originY: 'top' },
                { overlayX: 'start', overlayY: 'bottom' }
            )
        ];

        return this.overlay
            .position()
            .flexibleConnectedTo(this.origin)
            .withPositions(positions)
            .withFlexibleDimensions(false)
            .withPush(false);
    }

    private overlayClickOutside = (): Observable<MouseEvent> =>
    {
        return fromEvent<MouseEvent>(document, 'click')
            .pipe(
                filter((event: MouseEvent): boolean =>
                {
                    const clickTarget = event.target as HTMLElement;
                    const notOrigin = clickTarget !== this.origin;
                    const notOverlay = this.overlayRef && (this.overlayRef.overlayElement.contains(clickTarget) === false);

                    return notOrigin && notOverlay;
                }),
                takeUntil(this.overlayRef.detachments())
            );
    };
}
